import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon, PlainButton } from 'components/v3';
import { FONT_SYSTEM } from 'components/v3/tokens';
import {
  attentionMessage,
  isAnswerField,
  validateAnswers,
} from './answerValidation';
import { SectionHeader } from './inputs';
import { phone } from './palette';
import PreviewField from './PreviewField';
import { progressLabel, progressOf } from './progress';
import { groupSections } from './sections';

// The racer's in-app fill (build plan §5.7): header with the form name, the
// orange accent strip and "X of Y required · %"; then one full-width scroll
// grouped by section; Submit runs the same validation as submitForm. Errors
// show in red under each field plus an amber "N fields need your attention"
// card whose chips jump to each field. A valid submit shows "Entry
// submitted" — nothing is sent anywhere.
//
// `framed` draws the phone status bar and the app's decorative back / close
// icons (desktop, inside PhoneFrame). Unframed (mobile, full screen) leaves
// room at the top right for the modal's own close button. `headerAction`
// sits in the header's top row (the appearance switch).

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${(p) => phone(p.theme).screen};
  color: ${(p) => phone(p.theme).text};
  font-family: ${FONT_SYSTEM};
  transition: background 200ms ease;
`;

const StatusBar = styled.div`
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  padding: 0 30px 6px;
  font-size: 13px;
  font-weight: 600;
  background: ${(p) => phone(p.theme).chrome};
`;

const Header = styled.header`
  position: relative;
  flex-shrink: 0;
  padding: 10px 18px 12px;
  padding-top: ${(p) =>
    p.$framed ? '10px' : 'max(12px, env(safe-area-inset-top))'};
  background: ${(p) => phone(p.theme).chrome};
  border-bottom: 1px solid ${(p) => phone(p.theme).border2};
  z-index: 2;
`;

const Accent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: ${(p) => phone(p.theme).accent};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 32px;
  margin-bottom: 10px;
  padding-right: ${(p) => (p.$framed ? 0 : 52)}px;
  color: ${(p) => phone(p.theme).text2};
`;

const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
`;

const FormIcon = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => phone(p.theme).accentTint};
  color: ${(p) => phone(p.theme).accent};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.4px;
  color: ${(p) => phone(p.theme).text};
  overflow-wrap: anywhere;
`;

const Account = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: ${(p) => phone(p.theme).text3};
`;

const ProgressBlock = styled.div`
  margin-top: 11px;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => phone(p.theme).text3};

  span + span {
    color: ${(p) => phone(p.theme).accent};
  }
`;

const Track = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${(p) => phone(p.theme).border};
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${(p) => phone(p.theme).accent};
  transition: width 350ms ease;
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 18px 18px 30px;
  padding-bottom: ${(p) =>
    p.$framed ? '30px' : 'max(30px, env(safe-area-inset-bottom))'};
  line-height: 1.5;
`;

// Design: .phone .m-desc, the phone's body copy.
const Intro = styled.div`
  margin-bottom: 18px;
  font-size: 15px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${(p) => phone(p.theme).text2};
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  font-size: 13px;
  color: ${(p) => phone(p.theme).text3};
`;

const Group = styled.div`
  margin-bottom: 6px;
`;

const Attention = styled.div`
  margin-bottom: 16px;
  padding: 14px 15px;
  border-radius: 12px;
  background: ${(p) => phone(p.theme).warnBg};
  border: 1px solid ${(p) => phone(p.theme).warnBorder};
`;

const AttentionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => phone(p.theme).warnText};
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 11px;
`;

const AttentionChip = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => phone(p.theme).text};
  background: ${(p) => phone(p.theme).surface};
  border: 1px solid ${(p) => phone(p.theme).border};
`;

const SubmitButton = styled(PlainButton)`
  display: block;
  width: 100%;
  max-width: ${(p) => (p.$narrow ? '200px' : 'none')};
  margin-top: 8px;
  padding: 15px;
  border-radius: 10px;
  background: ${(p) => phone(p.theme).accent};
  color: white;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-align: center;
`;

const Success = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => phone(p.theme).successBg};
  color: ${(p) => phone(p.theme).successText};
`;

const SuccessTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
`;

const SuccessNote = styled.p`
  margin: 0;
  max-width: 260px;
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => phone(p.theme).text2};
`;

const text = (v) => (typeof v === 'string' ? v.trim() : '');

const fieldLabel = (field) => text(field && field.label) || 'Untitled field';

// Uploads are off in the preview, so a required file field can never be
// answered here; it is checked as optional (the label still shows the *).
const toCheckFields = (fields) =>
  fields.map((f) =>
    f.type === 'file_upload' && f.required === true
      ? { ...f, required: false }
      : f
  );

const errorMap = (errors) => {
  const out = {};
  errors.forEach(({ field, message }) => {
    if (!out[field]) out[field] = message;
  });
  return out;
};

const FIRST_FOCUSABLE =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const scrollBodyTo = (body, top) => {
  if (typeof body.scrollTo === 'function') {
    body.scrollTo({
      top,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  } else {
    body.scrollTop = top;
  }
};

const FillHeader = ({ form, progress, showProgress, framed, headerAction }) => {
  const account = text(form.account && form.account.name);
  return (
    <Header $framed={framed}>
      <Accent />
      <TopRow $framed={framed}>
        {framed ? <Icon name="arrow-left" size={20} /> : null}
        <TopRight>
          {headerAction}
          {framed ? <Icon name="x" size={20} /> : null}
        </TopRight>
      </TopRow>
      <TitleRow>
        <FormIcon>
          <Icon name="file-text" size={19} />
        </FormIcon>
        <div>
          <Title>{text(form.name) || 'Untitled Form'}</Title>
          {account ? <Account>{account}</Account> : null}
        </div>
      </TitleRow>
      {showProgress ? (
        <ProgressBlock>
          <ProgressText>
            <span>{progressLabel(progress)}</span>
            <span>{progress.pct}%</span>
          </ProgressText>
          <Track
            role="progressbar"
            aria-label="Required answers complete"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.pct}
            aria-valuetext={`${progressLabel(progress)}, ${progress.pct}%`}
          >
            <Fill style={{ width: `${progress.pct}%` }} />
          </Track>
        </ProgressBlock>
      ) : null}
    </Header>
  );
};

FillHeader.propTypes = {
  form: PropTypes.object.isRequired,
  progress: PropTypes.object.isRequired,
  showProgress: PropTypes.bool.isRequired,
  framed: PropTypes.bool.isRequired,
  headerAction: PropTypes.node,
};

FillHeader.defaultProps = { headerAction: null };

const AttentionCard = ({ errors, fieldsById, onJump }) => {
  const whole = errors.find((e) => e.field === 'answers');
  const chips = whole ? [] : errors;
  return (
    <Attention role="alert">
      <AttentionHead>
        <Icon name="triangle-alert" size={18} />
        <span>{whole ? whole.message : attentionMessage(errors.length)}</span>
      </AttentionHead>
      {chips.length ? (
        <ChipList>
          {chips.map((e) => (
            <AttentionChip key={e.field} onClick={() => onJump(e.field)}>
              {fieldLabel(fieldsById[e.field])}
              <Icon name="chevron-right" size={13} />
            </AttentionChip>
          ))}
        </ChipList>
      ) : null}
    </Attention>
  );
};

AttentionCard.propTypes = {
  errors: PropTypes.array.isRequired,
  fieldsById: PropTypes.object.isRequired,
  onJump: PropTypes.func.isRequired,
};

const SuccessView = ({ onDone }) => (
  <Success>
    <SuccessIcon>
      <Icon name="circle-check" size={40} />
    </SuccessIcon>
    <SuccessTitle>Entry submitted</SuccessTitle>
    <SuccessNote>Preview only. Nothing was saved.</SuccessNote>
    <SubmitButton $narrow onClick={onDone}>
      Done
    </SubmitButton>
  </Success>
);

SuccessView.propTypes = { onDone: PropTypes.func.isRequired };

const PreviewForm = ({ form, definition, assetUrls, framed, headerAction }) => {
  const [answers, setAnswers] = useState({});
  const [shownErrors, setShownErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scrollRequest, setScrollRequest] = useState(null);
  const bodyRef = useRef(null);
  const fieldRefs = useRef({});

  const fields = useMemo(
    () =>
      definition && Array.isArray(definition.fields)
        ? definition.fields.filter(Boolean)
        : [],
    [definition]
  );
  const checkFields = useMemo(() => toCheckFields(fields), [fields]);
  const fieldsById = useMemo(() => {
    const out = {};
    fields.forEach((f) => {
      out[f.id] = f;
    });
    return out;
  }, [fields]);
  const groups = useMemo(() => groupSections(fields), [fields]);

  // Only answers for this definition's answer fields are checked, so edits
  // to the working copy can never trip the "form has changed" rejection.
  const current = useMemo(() => {
    const out = {};
    Object.keys(answers).forEach((id) => {
      if (answers[id] !== undefined && isAnswerField(fieldsById[id])) {
        out[id] = answers[id];
      }
    });
    return out;
  }, [answers, fieldsById]);
  const live = useMemo(
    () => validateAnswers(checkFields, current),
    [checkFields, current]
  );
  const progress = progressOf(checkFields, live.errors);

  useEffect(() => {
    if (!scrollRequest) return;
    const body = bodyRef.current;
    const node = fieldRefs.current[scrollRequest.id];
    if (!body || !node) return;
    scrollBodyTo(body, Math.max(0, node.offsetTop - 14));
    if (scrollRequest.focus) {
      const target = node.querySelector(FIRST_FOCUSABLE);
      if (target) target.focus({ preventScroll: true });
    }
  }, [scrollRequest]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [submitted]);

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setShownErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const onSubmit = () => {
    const result = validateAnswers(checkFields, current);
    if (!result.errors.length) {
      setSubmitted(true);
      return;
    }
    setShownErrors(errorMap(result.errors));
    setAttempted(true);
    const first = result.errors.find((e) => fieldsById[e.field]);
    if (first) setScrollRequest({ id: first.field, focus: false });
  };

  const onDone = () => {
    setAnswers({});
    setShownErrors({});
    setAttempted(false);
    setSubmitted(false);
  };

  const renderField = (field) => (
    <PreviewField
      key={field.id}
      ref={(node) => {
        if (node) fieldRefs.current[field.id] = node;
        else delete fieldRefs.current[field.id];
      }}
      field={field}
      value={answers[field.id]}
      onChange={(value) => setAnswer(field.id, value)}
      error={shownErrors[field.id]}
      assetUrls={assetUrls}
    />
  );

  const description = text(form.description);

  return (
    <Root>
      {framed ? (
        <StatusBar aria-hidden="true">
          <span>9:41</span>
        </StatusBar>
      ) : null}
      <FillHeader
        form={form}
        progress={progress}
        showProgress={form.show_progress !== false && !submitted}
        framed={framed}
        headerAction={headerAction}
      />
      <Body ref={bodyRef} $framed={framed}>
        {submitted ? (
          <SuccessView onDone={onDone} />
        ) : (
          <>
            {description ? <Intro>{description}</Intro> : null}
            {fields.length ? null : (
              <Empty>Add fields in the builder to see them here.</Empty>
            )}
            {groups.map((group, index) => (
              <Group key={group.key}>
                {group.section ? (
                  <SectionHeader
                    section={group.section}
                    assetUrls={assetUrls}
                    first={index === 0}
                  />
                ) : null}
                {group.items.map(renderField)}
              </Group>
            ))}
            {attempted && live.errors.length ? (
              <AttentionCard
                errors={live.errors}
                fieldsById={fieldsById}
                onJump={(id) => setScrollRequest({ id, focus: true })}
              />
            ) : null}
            <SubmitButton onClick={onSubmit}>Submit Entry</SubmitButton>
          </>
        )}
      </Body>
    </Root>
  );
};

PreviewForm.propTypes = {
  form: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    account: PropTypes.shape({ name: PropTypes.string }),
    show_progress: PropTypes.bool,
  }).isRequired,
  definition: PropTypes.shape({
    schema: PropTypes.number,
    fields: PropTypes.array,
  }),
  assetUrls: PropTypes.object,
  framed: PropTypes.bool,
  headerAction: PropTypes.node,
};

PreviewForm.defaultProps = {
  definition: { schema: 1, fields: [] },
  assetUrls: {},
  framed: true,
  headerAction: null,
};

export default PreviewForm;
